import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ConsentLayout } from './ConsentLayout';
import { setConsentDraft } from '../../../lib/consent/consentDraft';

/**
 * Step 4 — privacy notice review (parent-side).
 *
 * Reached via the email accept_url. Renders a focused summary of the
 * privacy notice — controller, what we collect, lawful basis, who
 * processes data, retention, parent's rights — with a scroll gate
 * on the Continue button.
 *
 * The full version lives at /legal/privacy and is one click away. ICO
 * Children's Code §4 transparency requires the parent has had the
 * OPPORTUNITY to read; the scroll gate is the engineering enforcement
 * of that.
 *
 * Token + DOB query params are captured into the draft so Step 5+
 * (Wave 3.3) can correlate the parent's session with the child's.
 */
export function PrivacyReview() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  // Capture URL params into the draft on mount.
  useEffect(() => {
    const token = params.get('token');
    const dob = params.get('dob');
    if (token || dob) {
      setConsentDraft({
        ...(token ? { inviteToken: token } : {}),
        ...(dob ? { childDob: dob } : {}),
      });
    }
  }, [params]);

  // Detect scroll-to-bottom on the gated content area. Tolerance of
  // 8px catches subpixel rendering differences across browsers.
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const remaining = el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (remaining < 8) setScrolledToBottom(true);
  };

  const handleContinue = () => {
    setConsentDraft({ privacyNoticeRead: true });
    navigate('/parent/consent/yoti');
  };

  return (
    <ConsentLayout
      step={4}
      title="Before we go further — read this"
      lead="A short summary of how 4CM uses your child's data. Take a minute. The full notice is one click away."
    >
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="privacy-review"
        aria-label="Privacy notice summary"
      >
        <h2>Who we are</h2>
        <p>
          4CM is operated by <strong>4CM Ltd</strong> (registered in England &amp; Wales).
          Our Data Protection Officer can be reached at{' '}
          <a href="mailto:dpo@4cmcoaching.co.uk">dpo@4cmcoaching.co.uk</a>.
        </p>

        <h2>What we collect about your child</h2>
        <ul>
          <li>Name, email, date of birth, club + team</li>
          <li>Coach ratings across the four corners (technical, physical, psychological, social)</li>
          <li>Wellness logs — only if you and your child agree (health data, Art 9)</li>
          <li>Bio-band measurements (height + weight) — only with your explicit consent</li>
          <li>Video clips your coach saves — only with media consent</li>
          <li>AI-generated development notes — off by default for under-18s</li>
        </ul>

        <h2>What we don't do</h2>
        <ul>
          <li>We never sell your child's data.</li>
          <li>We never share it with advertisers.</li>
          <li>Bio-band data is never used to decide who makes a team.</li>
          <li>No identity recognition or face recognition, anywhere in the platform, ever.</li>
        </ul>

        <h2>Lawful basis (UK-GDPR Art 6 + 8 + 9)</h2>
        <p>
          For an under-13 child, we rely on <strong>your verifiable parental consent</strong>.
          Health data (wellness, bio-band) needs your explicit consent, separately. The
          full register is at <code>docs/legal/lawful-basis-register.md</code>.
        </p>

        <h2>Who processes data</h2>
        <p>
          Supabase (hosting, EU/Frankfurt), Anthropic (AI inference), Resend (transactional
          email), Sentry (crash reports — opt-in via cookie banner), PostHog (analytics —
          opt-in), Yoti (your identity verification — UK-based). All under signed Data
          Processing Agreements.
        </p>

        <h2>How long we keep it</h2>
        <ul>
          <li>Coach ratings: up to 3 years</li>
          <li>Wellness logs: 90 days default (max 12 months)</li>
          <li>Bio-band: 12 months default (max 3 years with explicit consent)</li>
          <li>Video tag metadata: 1 year default (max 2 years)</li>
          <li>Welfare incident records: until your child reaches age 25</li>
        </ul>

        <h2>Your rights</h2>
        <p>
          You can ask for a copy of everything we have, correct anything wrong, delete the
          account, pause processing, or object to specific uses — at any time. Statutory
          response is 30 days; we aim for 5 working days. Settings → Privacy after the
          account is set up.
        </p>

        <h2>Safeguarding</h2>
        <p>
          Coaches are DBS-checked and have completed FA Safeguarding training. Direct
          coach-to-minor messaging is blocked at the API level. Welfare concerns route to
          your club's Welfare Officer with statutory escalation pathways (LADO, NSPCC,
          police) where required.
        </p>

        <h2>How to contact us</h2>
        <p>
          Privacy + DSR queries: <a href="mailto:dpo@4cmcoaching.co.uk">dpo@4cmcoaching.co.uk</a>.
          Welfare concerns: <a href="mailto:welfare@4cmcoaching.co.uk">welfare@4cmcoaching.co.uk</a>.
          Right to complain to the ICO: <a href="https://ico.org.uk/make-a-complaint" target="_blank" rel="noreferrer">ico.org.uk</a>.
        </p>

        <p className="privacy-review__footnote">
          The full Article 13/14 notice is at{' '}
          <Link to="/legal/privacy" target="_blank" rel="noreferrer">/legal/privacy</Link>{' '}
          (opens in a new tab — won't lose your place).
        </p>
      </div>

      <div className="consent-form__actions">
        <button
          type="button"
          className="consent-form__btn"
          onClick={handleContinue}
          disabled={!scrolledToBottom}
        >
          {scrolledToBottom ? 'I have read this — continue' : 'Scroll to the end to continue'}
        </button>
      </div>
    </ConsentLayout>
  );
}
